import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Chip, CircularProgress, Alert,
  List, ListItem, ListItemIcon, ListItemText, IconButton, Tooltip, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NearMeIcon from '@mui/icons-material/NearMe';
import MapIcon from '@mui/icons-material/Map';
import StopIcon from '@mui/icons-material/Stop';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import apiClient from '../api/client';

const fmt = iso => iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

const diffMinutes = (a, b) => Math.round((new Date(a) - new Date(b)) / 60000);

const ScheduleStatus = ({ stop, now }) => {
  const arrival = new Date(stop.arrival_time);
  const departure = new Date(stop.departure_time);
  const minsToArrival = diffMinutes(arrival, now);
  const minsSinceDeparture = diffMinutes(now, departure);

  if (minsSinceDeparture > 0) {
    return <Chip label={`${minsSinceDeparture} min behind`} color="error" size="small" />;
  }
  if (minsToArrival > 0) {
    return <Chip label={`Arrive in ${minsToArrival} min`} color="info" size="small" />;
  }
  return <Chip label="At this stop" color="success" size="small" />;
};

const TodayView = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiClient.get('/plans/active')
      .then(r => setPlan(r.data.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh plan every 30s
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Tick clock every 10s
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(t);
  }, []);

  const handleDeactivate = async () => {
    await apiClient.post(`/plans/${plan.id}/deactivate`);
    setPlan(null);
  };

  const handleMarkShown = async (stop, undo = false) => {
    await apiClient.patch(`/plans/stops/${stop.id}/shown`, { undo });
    load();
  };

  const handleSkip = async (stop, undo = false) => {
    await apiClient.patch(`/plans/stops/${stop.id}/skip`, { undo });
    load();
  };

  const buildGoHereUrl = (stop) =>
    `https://www.google.com/maps/dir/?api=1&destination=${stop.property.latitude},${stop.property.longitude}&travelmode=driving`;

  const buildRouteFromUrl = (fromIndex, stops) => {
    const origin = `${stops[fromIndex].property.latitude},${stops[fromIndex].property.longitude}`;
    const remaining = stops.slice(fromIndex + 1);
    const destination = `${plan.start_lat},${plan.start_lng}`;
    const waypoints = remaining.map(s => `${s.property.latitude},${s.property.longitude}`).slice(0, 10).join('|');
    const params = new URLSearchParams({ api: '1', origin, destination, travelmode: 'driving' });
    if (waypoints) params.set('waypoints', waypoints);
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  };

  if (loading) return <CircularProgress sx={{ m: 4 }} />;

  if (!plan) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h5" gutterBottom>No active plan</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Activate a plan from a client's page to start your day.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/clients')}>Go to Clients</Button>
      </Box>
    );
  }

  const stops = plan.stops?.slice().sort((a, b) => a.stop_order - b.stop_order) ?? [];
  const currentIdx = stops.findIndex(s => !s.shown_at && !s.skipped_at);
  const currentStop = currentIdx >= 0 ? stops[currentIdx] : null;
  const allDone = stops.length > 0 && stops.every(s => s.shown_at || s.skipped_at);
  const shownCount = stops.filter(s => s.shown_at).length;
  const skippedCount = stops.filter(s => s.skipped_at).length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4">{plan.name}</Typography>
          <Typography color="text.secondary">
            {plan.client?.first_name} {plan.client?.last_name} · {shownCount} shown{skippedCount > 0 ? `, ${skippedCount} skipped` : ''} / {stops.length} total
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
          <Chip label="ACTIVE" color="success" size="small" />
          <Tooltip title="End day / deactivate plan">
            <Button variant="outlined" color="error" size="small" startIcon={<StopIcon />} onClick={handleDeactivate}>
              End Day
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {allDone ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          All stops complete! Great work today.
        </Alert>
      ) : currentStop && (
        /* Current stop hero card */
        <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="overline" color="primary">Now — Stop {currentIdx + 1}</Typography>
              <Typography variant="h5" fontWeight={700}>{currentStop.property?.address}</Typography>
              <Typography color="text.secondary">{currentStop.property?.city}, {currentStop.property?.state}</Typography>
              {currentStop.property?.mls_number && (
                <Typography variant="caption" color="text.secondary">MLS# {currentStop.property.mls_number}</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <ScheduleStatus stop={currentStop} now={now} />
              <Typography variant="body2">
                {fmt(currentStop.arrival_time)} – {fmt(currentStop.departure_time)} · {currentStop.duration_minutes} min
              </Typography>
              {currentStop.must_show && <Chip label="must show" size="small" color="warning" />}
              {currentStop.anchored && <Chip label="anchored" size="small" color="secondary" />}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => handleMarkShown(currentStop)}>
              Mark Shown
            </Button>
            <Button variant="outlined" color="warning" startIcon={<SkipNextIcon />} onClick={() => handleSkip(currentStop)}>
              Skip
            </Button>
            <Button variant="outlined" startIcon={<NearMeIcon />} onClick={() => window.open(buildGoHereUrl(currentStop), '_blank')}>
              Go Here
            </Button>
            <Button variant="outlined" startIcon={<MapIcon />} onClick={() => window.open(buildRouteFromUrl(currentIdx, stops), '_blank')}>
              Rest of Route
            </Button>
          </Box>
        </Paper>
      )}

      {/* Next stop preview */}
      {currentIdx >= 0 && currentIdx + 1 < stops.length && !stops[currentIdx + 1].shown_at && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="overline" color="text.secondary">Next — Stop {currentIdx + 2}</Typography>
          <Typography fontWeight={500}>{stops[currentIdx + 1].property?.address}, {stops[currentIdx + 1].property?.city}</Typography>
          <Typography variant="body2" color="text.secondary">
            Arrive {fmt(stops[currentIdx + 1].arrival_time)} · {stops[currentIdx + 1].duration_minutes} min
          </Typography>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Full stop list */}
      <Typography variant="h6" gutterBottom>All Stops</Typography>
      <List disablePadding>
        {stops.map((stop, i) => {
          const done = !!stop.shown_at;
          const skipped = !!stop.skipped_at;
          const resolved = done || skipped;
          const isCurrent = i === currentIdx;
          return (
            <ListItem
              key={stop.id}
              sx={{
                mb: 1, borderRadius: 1, border: '1px solid',
                borderColor: isCurrent ? 'primary.main' : 'divider',
                bgcolor: skipped ? 'warning.50' : resolved ? 'action.hover' : 'background.paper',
                opacity: resolved ? 0.7 : 1,
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {!resolved && (
                    <Tooltip title="Go here">
                      <IconButton size="small" onClick={() => window.open(buildGoHereUrl(stop), '_blank')}>
                        <NearMeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!done && (
                    <Tooltip title={skipped ? 'Undo skip' : 'Skip'}>
                      <IconButton size="small" color="warning" onClick={() => handleSkip(stop, skipped)}>
                        <SkipNextIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!skipped && (
                    <Tooltip title={done ? 'Undo' : 'Mark shown'}>
                      <IconButton size="small" color={done ? 'default' : 'success'} onClick={() => handleMarkShown(stop, done)}>
                        {done ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: skipped ? '#ff9800' : done ? '#9e9e9e' : isCurrent ? '#1976d2' : stop.must_show ? '#ed6c02' : '#90caf9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 'bold', fontSize: 12,
                }}>
                  {done ? '✓' : skipped ? '–' : i + 1}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <span style={{ textDecoration: resolved ? 'line-through' : 'none' }}>
                      {stop.property?.address}, {stop.property?.city}
                    </span>
                    {stop.property?.mls_number && <Chip label={`MLS# ${stop.property.mls_number}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                    {skipped && <Chip label="skipped" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />}
                  </Box>
                }
                secondary={
                  done ? `Shown at ${fmt(stop.shown_at)}`
                  : skipped ? `Skipped at ${fmt(stop.skipped_at)}`
                  : `${fmt(stop.arrival_time)} – ${fmt(stop.departure_time)} · ${stop.duration_minutes} min`
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default TodayView;
